import { useCallback, useRef, useState } from 'react';

import { useInterval, useMount } from 'react-use';
import { releaseTag as clientReleaseTag, isCI } from 'src/config';
import { useDispatchButtonSnack } from 'src/helpers';

import { fetchClientReleaseManifest } from './client-release-manifest';

// 1-minute check to ensure the client is up to date.
const VERSION_CHECK_INTERVAL_MS = 60 * 1000;
const CLIENT_VERSION = clientReleaseTag || 'dev';

export function useAppVersionChecker(): void {
  const hasNotifiedRef = useRef(false);
  const [versionCheckIntervalMs, setVersionCheckIntervalMs] = useState(
    !isCI ? VERSION_CHECK_INTERVAL_MS : null
  );
  const dispatchNewVersionDetected = useDispatchButtonSnack({
    buttonText: 'Reload',
    message: 'A new version of the application is available.',
    onClick: () => window.location.reload(),
    persist: true,
    variant: 'warning',
  });

  const checkForNewVersion = useCallback(async () => {
    if (hasNotifiedRef.current) return;

    const manifest = await fetchClientReleaseManifest();
    if (!manifest || manifest.releaseTag === CLIENT_VERSION) return;

    hasNotifiedRef.current = true;
    // Set interval to null to stop checking for new versions.
    setVersionCheckIntervalMs(null);
    dispatchNewVersionDetected();
  }, [dispatchNewVersionDetected]);

  useMount(() => {
    if (isCI) return;

    void checkForNewVersion();
  });

  useInterval(() => {
    void checkForNewVersion();
  }, versionCheckIntervalMs);
}
